import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';
import { supabase } from '../../supabase';
import { setStoredProfile } from '../../utils/session';
import AuthLayout, { ErrorBox, Spinner, authBtnStyle } from './AuthLayout';

function generateOtp() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

export default function ClientLogin() {
  const navigate = useNavigate();
  const [step, setStep]             = useState('phone'); // phone | otp
  const [phone, setPhone]           = useState('');
  const [otpCode, setOtpCode]       = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const handleSendOtp = () => {
    if (!phone.trim()) return;
    const otp = generateOtp();
    setGeneratedOtp(otp);
    // TODO: Africa's Talking SMS
    console.info(`[OTP simulé] ${phone} → ${otp}`);
    setStep('otp');
  };

  const handleVerifyOtp = async () => {
    if (otpCode !== generatedOtp) { setError('Code incorrect.'); return; }
    setError(''); setLoading(true);

    let { data: prof } = await supabase.from('profiles')
      .select('*').eq('phone', phone.trim()).eq('role', 'particulier').maybeSingle();

    if (!prof) {
      const { data: newProf } = await supabase.from('profiles')
        .insert({ phone: phone.trim(), role: 'particulier', account_status: 'approved' })
        .select().single();
      prof = newProf;
    }

    if (prof) setStoredProfile(prof);
    setLoading(false);
    navigate('/particulier');
  };

  // ── Saisie téléphone ──────────────────────────────────────────────
  if (step === 'phone') return (
    <AuthLayout footer="Wakaana Express · Suivi de livraisons">
      <h2 className="auth-title">Suivre mes livraisons</h2>
      <p className="auth-subtitle">Entrez votre numéro pour accéder à vos colis.</p>

      <div className="auth-field" style={{ marginBottom: 22 }}>
        <label className="auth-label">Numéro de téléphone</label>
        <div style={{ position: 'relative' }}>
          <span className="auth-fieldicon"><Phone size={15} /></span>
          <input
            className={`auth-input with-icon${error ? ' err' : ''}`}
            type="tel" value={phone} autoComplete="tel"
            onChange={e => { setPhone(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
            placeholder="+221 77 000 00 00"
            autoFocus
          />
        </div>
      </div>

      <ErrorBox error={error} />

      <button className="auth-btn" onClick={handleSendOtp} disabled={!phone.trim()} style={authBtnStyle(!!phone.trim())}>
        <ArrowRight size={16} /> RECEVOIR LE CODE
      </button>

      <p className="auth-foot" style={{ marginTop: 18 }}>
        Membre de l'équipe ?{' '}
        <button className="auth-foot-link" onClick={() => navigate('/login')}>
          Connexion
        </button>
      </p>
    </AuthLayout>
  );

  // ── Vérification OTP ──────────────────────────────────────────────
  return (
    <AuthLayout footer="Wakaana Express · Suivi de livraisons">
      <button className="auth-back" onClick={() => { setStep('phone'); setOtpCode(''); setError(''); }}>
        <ArrowLeft size={14} /> Changer de numéro
      </button>

      <div className="auth-note">
        <div className="auth-note-title">
          <ShieldCheck size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
          Code envoyé au {phone}
        </div>
        {process.env.NODE_ENV !== 'production' && (
          <div className="auth-note-sub">
            Mode test — code : <strong style={{ color: '#86efac' }}>{generatedOtp}</strong>
          </div>
        )}
      </div>

      <div className="auth-field">
        <label className="auth-label">Code à 6 chiffres</label>
        <input
          className={`auth-input${error ? ' err' : ''}`}
          type="tel" value={otpCode} maxLength={6}
          onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '')); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
          placeholder="000000"
          autoFocus
          style={{ fontSize: 24, letterSpacing: 10, textAlign: 'center', fontWeight: 700 }}
        />
      </div>

      <div style={{ height: 8 }} />
      <ErrorBox error={error} />

      <button className="auth-btn" onClick={handleVerifyOtp} disabled={loading || otpCode.length !== 6} style={authBtnStyle(!loading && otpCode.length === 6)}>
        {loading ? <Spinner size={18} light /> : <><ShieldCheck size={16} /> VALIDER</>}
      </button>
    </AuthLayout>
  );
}
